import React, { useState, useEffect, useRef } from 'react';

// @mui material components
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Slide from '@mui/material/Slide';
import Grow from '@mui/material/Grow';

// Otis Kit PRO components
import MKTypography from 'components/MKTypography';
import MKButton from 'components/MKButton';

// Iconos
import StarIcon from '@mui/icons-material/Star';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Imagen de fondo
import bgImage from 'assets/images/WorkWithUs.png';

function WorkWithUs() {
  // Estado para controlar cuándo mostrar la animación
  const [show, setShow] = useState(false);
  const wrapperRef = useRef(null);

  // Detectar cuando la sección entra en pantalla
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Solo activamos la animación una vez cuando sea visible
        if (entry.isIntersecting) {
          setShow(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Se activa cuando el 10% del componente es visible
    );

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const cardsData = [
    {
      icon: <StarIcon fontSize="medium" sx={{ color: '#ffffff !important' }} />,
      title: 'Calidad técnica',
      description:
        'En Dev Codes desarrollamos software con altos estándares de calidad, cuidando la arquitectura, el rendimiento y la mantenibilidad del código.',
    },
    {
      icon: <RocketLaunchIcon fontSize="medium" sx={{ color: '#ffffff !important' }} />,
      title: 'Innovación constante',
      description:
        'Nos movemos en un entorno tecnológico cambiante. Promovemos la exploración de nuevas herramientas, metodologías y enfoques modernos.',
    },
    {
      icon: <HandshakeIcon fontSize="medium" sx={{ color: '#ffffff !important' }} />,
      title: 'Colaboración en equipo',
      description:
        'Creemos en el trabajo colaborativo entre desarrolladores, diseñadores y clientes. Fomentamos una comunicación clara y respeto por las ideas.',
    },
    {
      icon: <TrendingUpIcon fontSize="medium" sx={{ color: '#ffffff !important' }} />,
      title: 'Crecimiento profesional',
      description:
        'Impulsamos el aprendizaje continuo y el crecimiento de nuestro equipo, brindando espacios para asumir nuevos retos y evolucionar profesionalmente.',
    },
  ];

  return (
    <Box
      ref={wrapperRef} // Referencia para el observador
      id="work-with-us"
      sx={{
        position: 'relative',
        height: 'auto',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        py: 4,
        mx: -2,
        overflow: 'hidden', // Importante para evitar scroll horizontal durante la animación
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(20, 30, 45, 0.85)',
          zIndex: 1,
        }}
      />
      <Container sx={{ position: 'relative', zIndex: 3 }}>
        <Grid container spacing={6} alignItems="center">
          {/* LADO IZQUIERDO: TEXTO (Slide desde la izquierda) */}
          <Grid item xs={12} lg={6}>
            <Slide direction="right" in={show} timeout={1000}>
              <Box>
                <MKTypography
                  variant="h3"
                  color="white"
                  fontWeight="bold"
                  textTransform="uppercase"
                  mb={2}
                >
                  Trabaja con nosotros
                </MKTypography>

                <MKTypography
                  variant="body2"
                  color="white"
                  opacity={0.9}
                  mb={2}
                  sx={{ lineHeight: 1.4 }}
                >
                  ¿Estás buscando nuevas oportunidades y retos profesionales donde puedas crecer,
                  aportar con tu talento y construir soluciones que realmente generen impacto?
                  <br />
                  En Dev Codes creemos en el poder de las personas y en el desarrollo continuo.
                  Buscamos desarrolladores y perfiles tecnológicos que quieran ir más allá del
                  código, que se identifiquen con una cultura basada en la colaboración, la
                  innovación y la mejora constante.
                </MKTypography>
                <Box component="ul" sx={{ listStyleType: 'disc', pl: 3, mb: 2 }}>
                  {[
                    'Valoramos la integridad, la responsabilidad y el compromiso.',
                    'Nos mueve la calidad, el aprendizaje continuo y el trabajo bien hecho.',
                    'Creemos en equipos humanos, dinámicos y diversos.',
                    'Trabajamos con clientes reales, retos reales y soluciones reales.',
                  ].map((item, idx) => (
                    <li key={idx} style={{ color: 'white' }}>
                      <MKTypography
                        variant="button"
                        color="white"
                        sx={{ lineHeight: 1.2, display: 'block' }}
                      >
                        {item}
                      </MKTypography>
                    </li>
                  ))}
                </Box>

                <MKTypography
                  variant="body2"
                  color="white"
                  opacity={0.9}
                  mb={3}
                  sx={{ lineHeight: 1.4 }}
                >
                  Queremos personas que no solo busquen un empleo, sino un lugar donde puedan crecer
                  profesionalmente, asumir nuevos desafíos y ser parte activa del crecimiento de la
                  organización.
                  <br />
                  Si sientes que este mensaje conecta contigo, queremos conocerte.
                </MKTypography>

                <MKButton
                  variant="contained"
                  color="info"
                  size="large"
                  component="a"
                  href="/Postulation"
                >
                  Quiero Postularme
                </MKButton>
              </Box>
            </Slide>
          </Grid>

          {/* LADO DERECHO: CARDS (Efecto Grow escalonado) */}
          <Grid item xs={12} lg={6}>
            <Grid container spacing={3}>
              {cardsData.map((card, index) => (
                <Grid item xs={12} md={6} key={index}>
                  {/* El timeout dinámico crea el efecto de cascada (uno tras otro) */}
                  <Grow in={show} timeout={800 + index * 500}>
                    <Card
                      sx={{
                        p: 3,
                        height: '100%',
                        backgroundColor: 'rgba(8, 226, 255, 0.13)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: 'none',
                        borderRadius: 3,
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0px 10px 20px rgba(255, 255, 255, 0.2)',
                          border: '2px solid #08E2FF',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <Box
                          sx={{
                            minWidth: '40px',
                            minHeight: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#2196f3',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mr: 2,
                          }}
                        >
                          {card.icon}
                        </Box>
                        <MKTypography variant="h6" color="white" fontWeight="bold">
                          {card.title}
                        </MKTypography>
                      </Box>
                      <MKTypography variant="body2" color="white" opacity={0.8} fontSize="0.85rem">
                        {card.description}
                      </MKTypography>
                    </Card>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default WorkWithUs;
